/* eslint-disable react/prop-types */
// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonButton from "components/ArgonButton";
import ArgonBadge from "components/ArgonBadge";
import ArgonTypography from "components/ArgonTypography";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import { Tooltip } from "@mui/material";

const formatDateTime = (value) => {
  if (!value) {
    return "N/A";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "N/A";
  }

  return parsed.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const renderAttachment = (url) => {
  if (!url) {
    return (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        Not uploaded
      </ArgonTypography>
    );
  }

  return (
    <ArgonButton
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      variant="outlined"
      color="info"
      size="small"
      sx={{ fontSize: "12px", py: 0.5, px: 1.5 }}
    >
      View
    </ArgonButton>
  );
};

const getTutor = (attendance) => {
  if (attendance.tutor) {
    return attendance.tutor;
  }

  const [sessionTutor] = attendance.classSession?.tutors || [];
  return sessionTutor || null;
};

const getStudentsLabel = (attendance) => {
  const students = attendance.classSession?.students || [];
  if (!students.length) {
    return "N/A";
  }

  if (students.length === 1) {
    return students[0].name || "N/A";
  }

  return `${students.length} Students`;
};

const getStatusColor = (status) => {
  switch (status) {
    case "COMPLETED":
      return "success";
    case "ONGOING":
    case "SCHEDULED":
      return "info";
    case "ON_HOLD":
      return "warning";
    case "CANCELLED":
      return "error";
    default:
      return "secondary";
  }
};

const AttendanceListViewTutorSummaryTableData = (
  attendanceList = [],
  handleEditClick,
  handleMarkAttendance,
  handleDeleteClick = null
) => {
  const columns = [
    { name: "sno", align: "center", width: "5%" },
    { name: "Date", align: "left", width: "15%" },
    { name: "Topic", align: "left", width: "15%" },
    { name: "Class Type", align: "left", width: "10%" },
    { name: "Tutor", align: "left", width: "15%" },
    { name: "Students", align: "left", width: "10%" },
    { name: "Class Status", align: "center", width: "10%" },
    { name: "Homework", align: "center", width: "8%" },
    { name: "Voice Note", align: "center", width: "8%" },
    { name: "Action", align: "center", width: "5%" },
  ];

  const rows = attendanceList.map((attendance, index) => {
    const tutor = getTutor(attendance);
    // Check if attendance is already marked (has topic covered)
    const isAttendanceMarked = attendance.topicCovered && attendance.topicCovered.trim() !== "";
    // If attendance is marked, status is COMPLETED
    // Else use attendance.status, else classSession.status, else CREATED
    let classStatus = "CREATED";
    if (isAttendanceMarked) {
      classStatus = "COMPLETED";
    } else if (attendance.status) {
      classStatus = attendance.status;
    } else if (attendance.classSession?.status) {
      classStatus = attendance.classSession.status;
    }

    // Check if the class is upcoming (attendance time is in the future)
    const isUpcoming =
      attendance.attendanceTime && new Date(attendance.attendanceTime) > new Date();
    // Only disable for upcoming (future) classes
    const disableAction = isUpcoming;

    return {
      sno: (
        <ArgonTypography
          variant="caption"
          color={disableAction ? "secondary" : "text"}
          fontWeight="medium"
        >
          {index + 1}
        </ArgonTypography>
      ),
      Date: (
        <ArgonTypography
          variant="caption"
          color={disableAction ? "secondary" : "text"}
          fontWeight="medium"
        >
          {formatDateTime(attendance.attendanceTime)}
        </ArgonTypography>
      ),
      Topic: (
        <ArgonTypography
          variant="caption"
          color={disableAction ? "secondary" : "text"}
          fontWeight="medium"
        >
          {attendance.topicCovered || "N/A"}
        </ArgonTypography>
      ),
      "Class Type": (
        <ArgonTypography
          variant="caption"
          color={disableAction ? "secondary" : "text"}
          fontWeight="medium"
        >
          {attendance.type || "N/A"}
        </ArgonTypography>
      ),
      Tutor: (
        <ArgonBox display="flex" flexDirection="column">
          <ArgonTypography
            variant="caption"
            fontWeight="medium"
            color={disableAction ? "secondary" : "text"}
          >
            {tutor?.name || tutor?.fullName || "N/A"}
          </ArgonTypography>
          {(tutor?.email || tutor?.phone) && (
            <ArgonTypography variant="caption" color="secondary">
              {tutor?.email || tutor?.phone}
            </ArgonTypography>
          )}
        </ArgonBox>
      ),
      Students: (
        <ArgonTypography
          variant="caption"
          color={disableAction ? "secondary" : "text"}
          fontWeight="medium"
        >
          {getStudentsLabel(attendance)}
        </ArgonTypography>
      ),
      "Class Status": (
        <ArgonBadge
          variant="gradient"
          badgeContent={classStatus}
          color={getStatusColor(classStatus)}
          size="xs"
          container
        />
      ),
      Homework: renderAttachment(attendance.homeWork),
      "Voice Note": renderAttachment(attendance.voiceNote),
      Action: (
        <ArgonBox display="flex" gap={1} justifyContent="center">
          {/* Mark Attendance: always render, only disable for upcoming or already marked */}
          <Tooltip
            title={
              isAttendanceMarked
                ? "Already marked"
                : disableAction
                ? "Cannot mark upcoming class"
                : "Mark Attendance"
            }
            placement="top"
          >
            <span>
              <ArgonButton
                variant="text"
                size="small"
                onClick={() => handleMarkAttendance && handleMarkAttendance(attendance)}
                sx={{ minWidth: "32px", p: 0 }}
                disabled={disableAction || isAttendanceMarked}
              >
                <AddCircleIcon
                  color={disableAction || isAttendanceMarked ? "disabled" : "success"}
                  fontSize="small"
                />
              </ArgonButton>
            </span>
          </Tooltip>
          {/* Edit: always enabled for all classes */}
          <Tooltip title="Edit" placement="top">
            <span>
              <ArgonButton
                variant="text"
                size="small"
                onClick={() => handleEditClick && handleEditClick(attendance)}
                sx={{ minWidth: "32px", p: 0 }}
              >
                <EditIcon color="info" fontSize="small" />
              </ArgonButton>
            </span>
          </Tooltip>
          {/* Delete: always enabled for all classes */}
          <Tooltip title="Delete" placement="top">
            <span>
              <ArgonButton
                variant="text"
                size="small"
                onClick={() => handleDeleteClick && handleDeleteClick(attendance)}
                sx={{ minWidth: "32px", p: 0 }}
              >
                <DeleteIcon color="error" fontSize="small" />
              </ArgonButton>
            </span>
          </Tooltip>
        </ArgonBox>
      ),
    };
  });

  return { columns, rows };
};

export default AttendanceListViewTutorSummaryTableData;
